import type {
  BuyerSubmissionDetailDto,
  BuyerSubmissionEscalationPreviewDto,
} from "@/api/buyers";

/**
 * What escalation requires, which of those things are in the way, and who
 * clears each one.
 *
 * <p>🔴 <b>ONE computation, two surfaces.</b> The escalation tab renders this as
 * a table with a door per row; Overview renders the same answer as a read-only
 * summary that links here. They are the same function because two places
 * narrating "what is blocking this" from separate logic drift — and this one
 * would drift in front of a specialist mid-call, with a listing broker waiting
 * on the other end. Pure, so neither surface has to own a query to use it.</p>
 *
 * <p>🔴 <b>It composes no refusal copy.</b> The server owns the words: the
 * pre-check and the refused POST are single-sourced in
 * <c>BuyerSubmissionEscalationBlockedException</c> precisely so they cannot say
 * different things, and re-wording a refusal here would reopen that. What this
 * module DOES author is the row's own furniture — the requirement's name and
 * who clears it — which is table furniture rather than a refusal. The owner
 * labels are read straight off the server's messages ("ask a Broker Marketer to
 * convert the lead" → a Broker Marketer), so even the furniture traces back to
 * one source.</p>
 *
 * <h3>The three states, and why "unchecked" is not a rounding error</h3>
 *
 * <p>The server's gate is a SEQUENTIAL walk — listing agent → Broker → liaison —
 * that returns at the first refusal. So when a listing has no agent, the preview
 * reports <c>brokerId: null</c> and <c>brokerHasLiaison: false</c> not because
 * those were checked and failed, but because the walk stopped before reaching
 * them. Rendering those as blockers would tell a specialist to go ask a Broker
 * Manager to assign a liaison to a broker that does not exist yet — a confident
 * instruction to do something impossible, which is worse than saying nothing.
 * So a requirement downstream of a live blocker reports <c>unchecked</c>, and
 * the surface says it has not been reached yet.</p>
 *
 * <p>Two requirements are exempt from that, because the server evaluates them
 * independently of the walk: the LOI is read straight off the submission, and
 * the seller prequalification is a separate advisory read.</p>
 */

/** The requirements, in the server's own walk order. */
export type EscalationRequirementKey =
  | "listing-agent"
  | "broker"
  | "liaison"
  | "loi"
  | "seller-prequalification";

/**
 * `met` — satisfied. `blocked` — checked, and in the way. `unchecked` — NOT
 * checked, because an earlier blocker stopped the walk (or, for the seller
 * prequalification, because the advisory read did not run). Never collapse
 * `unchecked` into `blocked`: see the class note above.
 */
export type EscalationRequirementState = "met" | "blocked" | "unchecked";

/**
 * `blocking` refuses the hand-off. `advisory` never does — the seller
 * prequalification depends on a third party Phoenix cannot compel, so it warns
 * and the escalation proceeds. That is a locked product decision, and a surface
 * that lets an advisory disable the action has broken it.
 */
export type EscalationRequirementSeverity = "blocking" | "advisory";

export interface EscalationRequirement {
  key: EscalationRequirementKey;
  /** The requirement's name. Table furniture, never a refusal. */
  label: string;
  /** Who clears it, phrased as the server's own message phrases it. */
  owner: string;
  state: EscalationRequirementState;
  severity: EscalationRequirementSeverity;
  /**
   * The server's message, VERBATIM, or null.
   *
   * <p>Non-null on exactly one row: the earliest blocking requirement, which is
   * the one the server composed <c>blockedReason</c> for. Every other row shows
   * its name and its state and no invented prose — because the server did not
   * compose prose for it, and writing some here is how the pre-check and the
   * refusal start disagreeing.</p>
   *
   * <p>The seller-prequalification row carries its own verbatim warning, which
   * the server composes separately and always supplies when it applies.</p>
   */
  message: string | null;
}

export interface EscalationReadiness {
  /**
   * Whether the gate has been read at all. False while the preview is loading
   * or after it failed — and then every requirement is `unchecked` and
   * `canEscalate` is false, so a surface cannot accidentally report a submission
   * as ready, or as blocked, on the strength of an answer it never received.
   */
  resolved: boolean;
  /** Already handed off. The answer is "go look at it", not "fix something". */
  alreadyEscalated: boolean;
  /** The server's own verdict, never re-derived from the rows. */
  canEscalate: boolean;
  /** Every requirement, in walk order, whatever its state. */
  requirements: EscalationRequirement[];
  /** The blocking ones that are actually in the way. */
  blocking: EscalationRequirement[];
  /** The advisory ones that are outstanding. Never disable an action on these. */
  advisories: EscalationRequirement[];
  /** One sentence both surfaces show, so the two cannot narrate differently. */
  headline: string;
  brokerName: string | null;
  listingAgentName: string | null;
}

/**
 * Who clears each requirement.
 *
 * <p>Read off the server's own refusal messages so the two agree: "ask a Broker
 * Marketer to convert the lead", "Ask a Broker Manager to assign one", "Upload
 * it to this submission's documents". The broker row names both parties in
 * order because clearing it genuinely takes both — the specialist escalates the
 * agent into recruitment, and a Marketer converts the resulting lead.</p>
 */
const OWNERS: Record<EscalationRequirementKey, string> = {
  "listing-agent": "You",
  broker: "You, then a Broker Marketer",
  liaison: "A Broker Manager",
  loi: "You",
  "seller-prequalification": "The seller",
};

const LABELS: Record<EscalationRequirementKey, string> = {
  "listing-agent": "An agent on the listing",
  broker: "That agent onboarded as a Phoenix broker",
  liaison: "A Broker Liaison on that broker",
  loi: "A signed letter of intent",
  "seller-prequalification": "The seller prequalified",
};

const ORDER: EscalationRequirementKey[] = [
  "listing-agent",
  "broker",
  "liaison",
  "loi",
  "seller-prequalification",
];

const SEVERITY: Record<EscalationRequirementKey, EscalationRequirementSeverity> = {
  "listing-agent": "blocking",
  broker: "blocking",
  liaison: "blocking",
  loi: "blocking",
  // 🔴 Advisory, and this line is the one that must never change. A seller
  // prequalification depends on a third party Phoenix cannot compel.
  "seller-prequalification": "advisory",
};

/**
 * Reads the gate into the readiness a surface can render.
 *
 * @param s The submission. Supplies the already-escalated fact independently of
 *   the gate, so a surface holding only the record still gets a truthful answer
 *   while the preview is in flight.
 * @param gate The escalation preview. `null`/`undefined` while it is loading or
 *   after it failed — which yields `resolved: false` rather than a guess.
 */
export function computeEscalationReadiness(
  s: BuyerSubmissionDetailDto,
  gate: BuyerSubmissionEscalationPreviewDto | null | undefined,
): EscalationReadiness {
  // Read from the RECORD as well as the gate. The record is what a surface has
  // in hand first, and the two cannot disagree: escalation stamps the status and
  // the pointer in one SaveChanges.
  const alreadyEscalated =
    Boolean(s.escalation) || s.status === "EscalatedToDeal" || Boolean(gate?.alreadyEscalated);

  if (!gate) {
    return {
      resolved: false,
      alreadyEscalated,
      canEscalate: false,
      requirements: ORDER.map((key) => unchecked(key)),
      blocking: [],
      advisories: [],
      headline: alreadyEscalated
        ? "This submission has been escalated."
        : "Checking what escalation needs…",
      brokerName: null,
      listingAgentName: null,
    };
  }

  if (alreadyEscalated) {
    // Nothing is "blocking" a hand-off that already happened, and listing the
    // preconditions again would read as work still owed.
    return {
      resolved: true,
      alreadyEscalated: true,
      canEscalate: false,
      requirements: ORDER.map((key) => unchecked(key)),
      blocking: [],
      advisories: [],
      headline: "This submission has been escalated.",
      brokerName: gate.brokerName ?? null,
      listingAgentName: gate.listingAgentName ?? null,
    };
  }

  const hasAgent = Boolean(gate.listingAgentName);
  const hasBroker = Boolean(gate.brokerId);

  // The walk, reproduced as three states rather than one. `unchecked` propagates
  // forward: once something is in the way, everything the server would only
  // have looked at afterwards is genuinely unknown.
  const agentState: EscalationRequirementState = hasAgent ? "met" : "blocked";
  const brokerState: EscalationRequirementState = !hasAgent
    ? "unchecked"
    : hasBroker
      ? "met"
      : "blocked";
  const liaisonState: EscalationRequirementState = !hasAgent || !hasBroker
    ? "unchecked"
    : gate.brokerHasLiaison
      ? "met"
      : "blocked";

  // 🔴 Exempt from the walk. `HasSignedLoi` is read straight off the submission
  // before the gate runs, so it is known even when an earlier check refused.
  const loiState: EscalationRequirementState = gate.hasSignedLoi ? "met" : "blocked";

  const requirements: EscalationRequirement[] = [
    row("listing-agent", agentState),
    row("broker", brokerState),
    row("liaison", liaisonState),
    row("loi", loiState),
    sellerRow(gate),
  ];

  // 🔴 The verbatim refusal lands on exactly one row. `blockedReason` is what
  // the server composed for the FIRST thing in its way, and the walk order here
  // is the same order, so the first blocking row is definitionally the one it
  // belongs to. Attaching it anywhere else would caption a row with a message
  // about a different requirement.
  const firstBlocking = requirements.find(
    (r) => r.severity === "blocking" && r.state === "blocked",
  );
  if (firstBlocking && gate.blockedReason) firstBlocking.message = gate.blockedReason;

  const blocking = requirements.filter(
    (r) => r.severity === "blocking" && r.state === "blocked",
  );
  const advisories = requirements.filter(
    (r) => r.severity === "advisory" && r.state === "blocked",
  );

  return {
    resolved: true,
    alreadyEscalated: false,
    // 🔴 The SERVER's verdict, not a re-derivation from the rows above. The rows
    // are a reading of the gate for a human; `canEscalate` is the gate. If they
    // ever disagree, the server is right and this surface is stale.
    canEscalate: gate.canEscalate,
    requirements,
    blocking,
    advisories,
    headline: headlineFor(gate.canEscalate, blocking, advisories),
    brokerName: gate.brokerName ?? null,
    listingAgentName: gate.listingAgentName ?? null,
  };
}

function row(
  key: EscalationRequirementKey,
  state: EscalationRequirementState,
): EscalationRequirement {
  return {
    key,
    label: LABELS[key],
    owner: OWNERS[key],
    state,
    severity: SEVERITY[key],
    message: null,
  };
}

function unchecked(key: EscalationRequirementKey): EscalationRequirement {
  return row(key, "unchecked");
}

/**
 * The seller prequalification, whose three states map differently.
 *
 * <p>The server returns no warning in TWO different situations: a clean pass,
 * and a check that did not run — the hook is optional, and an advisory read that
 * throws is swallowed on purpose so it can never become the gate this design
 * rejects. `sellerPrequalificationLinked` is what tells them apart, and they
 * must be told apart: reporting "not established either way" as a clean pass
 * would put a satisfied tick against a question nobody asked.</p>
 */
function sellerRow(gate: BuyerSubmissionEscalationPreviewDto): EscalationRequirement {
  const warning = gate.sellerPrequalificationWarning?.trim();
  if (warning) {
    const r = row("seller-prequalification", "blocked");
    r.message = gate.sellerPrequalificationWarning!;
    return r;
  }
  return row(
    "seller-prequalification",
    gate.sellerPrequalificationLinked ? "met" : "unchecked",
  );
}

function headlineFor(
  canEscalate: boolean,
  blocking: EscalationRequirement[],
  advisories: EscalationRequirement[],
): string {
  if (blocking.length > 0) {
    return blocking.length === 1
      ? `One thing is in the way: ${lower(blocking[0].label)}.`
      : `${blocking.length} things are in the way.`;
  }
  if (!canEscalate) {
    // The server refused for a reason these rows do not model — a template that
    // is not configured, say. Say so rather than reporting "ready" off a row
    // count that happens to be zero.
    return "Escalation is not available yet.";
  }
  if (advisories.length > 0) {
    return "Ready to escalate, with one thing worth knowing first.";
  }
  return "Ready to escalate.";
}

function lower(label: string): string {
  return label.charAt(0).toLowerCase() + label.slice(1);
}
